import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/browser';
import format from 'string-template';
import { Camera, ImageIcon } from 'lucide-react';

import { FILTERS } from '../core/filters';
import { STICKER_FRAMES } from '../core/stickerFrames';
import texts from '../texts.json';
import {
  Actions,
  Body,
  Intro,
  Option,
  OptionRow,
  OptionThumb,
  PhotoSheet,
  PhotoSheetOption,
  PhotoSheetTitle,
  Picker,
  PrimaryButton,
  StatusText,
  StickerImage,
  StickerPicker,
  StickerPlaceholder,
  Tab,
  Tabs,
  TagDot,
  TaskTag,
  TextButton,
  Title,
} from './styles';

const PhotoStatus = {
  IDLE: 'idle',
  LOADING: 'loading',
  FAILED: 'failed',
};

const PickerTab = {
  FILTER: 'filter',
  FRAME: 'frame',
};

const STICKER_WIDTH = 196;

/*
  Task 1's one screen: the sticker itself is the photo picker, and under it the filter and frame pickers. The
  sticker on screen is the drawing that gets shared.
*/
const MakeStep = ({
  challengeName,
  stickerUrl,
  hasPhoto,
  isStoryReady,
  filterId,
  frameId,
  filterPreviews,
  framePreviews,
  onPhoto,
  onFilterChange,
  onFrameChange,
  onCreate,
  onSkip,
  sheetContainer,
}) => {
  const [status, setStatus] = useState(PhotoStatus.IDLE);
  const [tab, setTab] = useState(PickerTab.FILTER);
  const [isPhotoSheetOpen, setIsPhotoSheetOpen] = useState(false);

  const onFileChange = async (event) => {
    const [file] = event.target.files;
    // Cleared, so picking the same photo again still counts as a change.
    // eslint-disable-next-line no-param-reassign
    event.target.value = '';
    if (!file) {
      return;
    }
    setStatus(PhotoStatus.LOADING);
    try {
      await onPhoto(file);
      setStatus(PhotoStatus.IDLE);
    } catch (error) {
      Sentry.captureException(error, { extra: { fileType: file.type } });
      setStatus(PhotoStatus.FAILED);
    }
  };

  const options = tab === PickerTab.FILTER
    ? FILTERS.map(({ id }) => ({
      id,
      label: texts.filters[id],
      preview: filterPreviews[id],
      isSelected: id === filterId,
      onClick: () => onFilterChange(id),
    }))
    : STICKER_FRAMES.map(({ id }) => ({
      id,
      label: texts.frames[id],
      preview: framePreviews[id],
      isSelected: id === frameId,
      onClick: () => onFrameChange(id),
    }));

  const isLoading = status === PhotoStatus.LOADING;
  const photoLabel = hasPhoto ? texts.make.changePhoto : texts.make.pickPhoto;
  const closePhotoSheet = () => setIsPhotoSheetOpen(false);
  const sticker = stickerUrl
    ? <StickerImage src={stickerUrl} alt={texts.done.sticker} $width={STICKER_WIDTH} $tilt={-2} />
    : <StickerPlaceholder $width={STICKER_WIDTH} />;

  return (
    <>
      <Intro>
        <TaskTag>
          <TagDot />
          {format(texts.taskTag, { challengeName: challengeName.toUpperCase() })}
        </TaskTag>
        <Title $size={31}>{texts.make.title}</Title>
        <Body>{texts.make.body}</Body>
      </Intro>
      <StickerPicker
        as="button"
        type="button"
        aria-label={photoLabel}
        onClick={() => setIsPhotoSheetOpen(true)}
        disabled={isLoading}
      >
        {sticker}
      </StickerPicker>
      {/*
        A sheet asks first, since Android's Chrome no longer offers the camera in its photo picker. It opens
        inside the flow, so it keeps the flow's colors and font, and stays mounted when closed, so the picker it
        opened still reports the photo.
      */}
      <PhotoSheet
        anchor="bottom"
        open={isPhotoSheetOpen}
        onClose={closePhotoSheet}
        container={() => sheetContainer.current}
        keepMounted
        PaperProps={{ role: 'dialog', 'aria-label': photoLabel }}
      >
        <PhotoSheetTitle>{photoLabel}</PhotoSheetTitle>
        <PhotoSheetOption onClick={closePhotoSheet}>
          <Camera size={20} aria-hidden="true" />
          {texts.make.takePhoto}
          <input type="file" accept="image/*" capture="user" onChange={onFileChange} />
        </PhotoSheetOption>
        <PhotoSheetOption onClick={closePhotoSheet}>
          <ImageIcon size={20} aria-hidden="true" />
          {texts.make.choosePhoto}
          <input type="file" accept="image/*" onChange={onFileChange} />
        </PhotoSheetOption>
        <TextButton type="button" onClick={closePhotoSheet}>{texts.make.cancel}</TextButton>
      </PhotoSheet>
      {isLoading && <StatusText role="status">{texts.make.loading}</StatusText>}
      {status === PhotoStatus.FAILED && <StatusText role="alert">{texts.make.error}</StatusText>}
      <Picker>
        <Tabs role="tablist">
          {Object.values(PickerTab).map((id) => (
            <Tab
              key={id}
              type="button"
              role="tab"
              id={`story-${id}-tab`}
              aria-selected={tab === id}
              aria-controls="story-options"
              onClick={() => setTab(id)}
            >
              {texts.make[id]}
            </Tab>
          ))}
        </Tabs>
        <OptionRow role="tabpanel" id="story-options" aria-labelledby={`story-${tab}-tab`}>
          {options.map(({
            id,
            label,
            preview,
            isSelected,
            onClick,
          }) => (
            <Option key={id} type="button" aria-pressed={isSelected} onClick={onClick}>
              <OptionThumb $isSelected={isSelected} $fit={tab === PickerTab.FILTER ? 'cover' : 'contain'}>
                {!!preview && <img src={preview} alt="" />}
              </OptionThumb>
              {label}
            </Option>
          ))}
        </OptionRow>
      </Picker>
      <Actions>
        <PrimaryButton type="button" disabled={!isStoryReady} onClick={onCreate}>{texts.make.create}</PrimaryButton>
        <TextButton type="button" onClick={onSkip}>{texts.skipForNow}</TextButton>
      </Actions>
    </>
  );
};

MakeStep.propTypes = {
  challengeName: PropTypes.string.isRequired,
  stickerUrl: PropTypes.string,
  hasPhoto: PropTypes.bool.isRequired,
  isStoryReady: PropTypes.bool.isRequired,
  filterId: PropTypes.string.isRequired,
  frameId: PropTypes.string.isRequired,
  filterPreviews: PropTypes.objectOf(PropTypes.string).isRequired,
  framePreviews: PropTypes.objectOf(PropTypes.string).isRequired,
  onPhoto: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onFrameChange: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
  sheetContainer: PropTypes.shape({ current: PropTypes.instanceOf(Element) }).isRequired,
};

MakeStep.defaultProps = {
  stickerUrl: null,
};

export default MakeStep;
